import { useState, useEffect } from "react";
import api from "../../../utils/api";
import { toast } from "react-toastify";
import { validatePrices, buildUpdatesArray } from "../utils/priceUtils";
import {
  DEFAULT_PAGE_SIZE,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  VALIDATION_MESSAGES,
  ONE_DAY_IN_MS,
} from "../utils/constants";

/**
 * Custom hook for price update logic
 * Manages state and side effects for price updates
 *
 * @returns {Object} Hook state and handlers
 * @returns {Array} returns.products - List of products
 * @returns {Object} returns.editedPrices - Edited prices state
 * @returns {string} returns.searchTerm - Search term
 * @returns {boolean} returns.showRecentlyUpdatedOnly - Filter flag
 * @returns {boolean} returns.isCompactView - View mode flag
 * @returns {number} returns.page - Current page number
 * @returns {boolean} returns.hasMore - Has more pages flag
 * @returns {Object} returns.priceErrors - Validation errors
 * @returns {Function} returns.setSearchTerm - Set search term
 * @returns {Function} returns.setShowRecentlyUpdatedOnly - Set filter
 * @returns {Function} returns.setIsCompactView - Set view mode
 * @returns {Function} returns.setPage - Set page number
 * @returns {Function} returns.handleNewPriceChange - Handle price change
 * @returns {Function} returns.handleSubmit - Submit price updates
 * @returns {Function} returns.fetchAllProductsForExport - Fetch all products
 * @returns {Function} returns.getFilteredProducts - Get filtered products
 */
export const usePriceUpdate = () => {
  const [products, setProducts] = useState([]);
  const [editedPrices, setEditedPrices] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [showRecentlyUpdatedOnly, setShowRecentlyUpdatedOnly] = useState(false);
  const [isCompactView, setIsCompactView] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(1000); // Fetch all products for frontend pagination
  const [hasMore, setHasMore] = useState(true);
  const [priceErrors, setPriceErrors] = useState({});

  // Fetch products when dependencies change
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const params = new URLSearchParams();
        params.append("limit", limit);
        params.append("showAll", "true");
        params.append("sortBy", "newest");
        if (showRecentlyUpdatedOnly)
          params.append("showRecentlyUpdatedOnly", "true");
        if (searchTerm) params.append("search", searchTerm);

        const response = await api.get(`/products?${params.toString()}`);
        setProducts(response.data.products);
        setHasMore(false); // Not using backend pagination
      } catch (err) {
        console.error("Error fetching products:", err);
        toast.error(ERROR_MESSAGES.FETCH_FAILED);
      }
    };

    fetchProducts();
  }, [limit, showRecentlyUpdatedOnly, searchTerm]);

  // Reset to page 1 when search term or filter changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, showRecentlyUpdatedOnly]);

  /**
   * Handle price input changes
   * Updates state and validates prices
   *
   * @param {string} productId - Product ID
   * @param {string} [variantId] - Variant ID (optional)
   * @param {string} value - New value
   * @param {string} field - Field name (newPrice or newOfferPrice)
   */
  const handleNewPriceChange = (productId, variantId, value, field) => {
    setEditedPrices((prev) => {
      let updatedPrices;

      if (variantId) {
        updatedPrices = {
          ...prev,
          [productId]: {
            ...(prev[productId] || {}),
            [variantId]: {
              ...(prev[productId]?.[variantId] || {}),
              [field]: value,
            },
          },
        };
      } else {
        updatedPrices = {
          ...prev,
          [productId]: {
            ...(prev[productId] || {}),
            [field]: value,
          },
        };
      }

      // Validate prices after update
      validatePrices(
        productId,
        variantId,
        updatedPrices,
        products,
        setPriceErrors,
      );

      return updatedPrices;
    });
  };

  /**
   * Remove offer price 
   */
  const handleRemoveOfferPrice = async (productId, variantId = null) => {
    try {
      const payload = variantId
        ? [{ productId, variantId, newOfferPrice: null }]
        : [{ productId, newOfferPrice: null }];

      await api.post("/products/update-prices", payload);

      // Update local products state 
      setProducts((prev) =>
        prev.map((product) => {
          if (product._id !== productId) return product;

          if (variantId) {
            return {
              ...product,
              variants: product.variants.map((variant) =>
                variant._id === variantId
                  ? { ...variant, offerPrice: null }
                  : variant
              ),
            };
          }

          return { ...product, offerPrice: null };
        })
      );

      toast.success("Offer price removed");
    } catch (err) {
      toast.error("Failed to remove offer price");
      console.error(err);
    }
  };



  /**
   * Submit price updates to backend
   */
  /**
   * Submit price updates to backend
   * Validates and sends updates to API
   */
  const handleSubmit = async () => {
    if (Object.keys(priceErrors).length > 0) {
      toast.error(VALIDATION_MESSAGES.FIX_ERRORS);
      return;
    }

    try {
      const updates = buildUpdatesArray(editedPrices);

      if (updates.length === 0) {
        toast.warning(VALIDATION_MESSAGES.NO_CHANGES);
        return;
      }

      await api.post("/products/update-prices", updates);
      toast.success(SUCCESS_MESSAGES.PRICES_UPDATED);
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      const message =
        err.response?.data?.message || ERROR_MESSAGES.UPDATE_FAILED;
      console.error("Error updating prices:", err);
      toast.error(message);
    }
  };

  /**
   * Fetch all products for export
   *
   * @returns {Promise<Array>} Array of all products
   */
  const fetchAllProductsForExport = async () => {
    try {
      const response = await api.get("/products?limit=1000&showAll=true&sortBy=newest");
      return response.data.products;
    } catch (err) {
      console.error("Error fetching all products for export:", err);
      toast.error(ERROR_MESSAGES.EXPORT_FAILED);
      return [];
    }
  };

  /**
   * Filter products based on search and date filter
   *
   * @returns {Array} Filtered products array
   */
  const getFilteredProducts = () => {
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - ONE_DAY_IN_MS);

    return Array.isArray(products)
      ? products.filter((product) => {
        const search = searchTerm.toLowerCase();
        const matchesSearch =
          product.productName.toLowerCase().includes(search) ||
          product.categoryName.toLowerCase().includes(search) ||
          product.subcategoryName.toLowerCase().includes(search) ||
          product.productNumber?.toString().toLowerCase().includes(search);
        const isRecentlyUpdated =
          !showRecentlyUpdatedOnly ||
          new Date(product.updatedAt) >= oneDayAgo;
        return matchesSearch && isRecentlyUpdated;
      })
      : [];
  };

  /**
   * Toggle product or variant visibility
   */
  const handleToggleVisibility = async (productId, variantId, currentVisibility) => {
    try {
      const newVisibility = !currentVisibility;
      const productToUpdate = products.find(p => p._id === productId);
      
      if (!productToUpdate) return;
  
      await api.patch(`/products/${productId}/toggle-visibility`, {
        isVisible: newVisibility
      });
      
      // Update local state - ALWAYS apply to the whole product
      setProducts((prev) =>
        prev.map((product) => {
          if (product._id !== productId) return product;
          return { ...product, isVisible: newVisibility };
        })
      );
      
      toast.success(`Product ${newVisibility ? "enabled" : "disabled"} successfully`);
    } catch (err) {
      console.error("Error updating visibility:", err);
      toast.error("Failed to update product visibility");
    }
  };

  return {
    // State
    products,
    editedPrices,
    searchTerm,
    showRecentlyUpdatedOnly,
    isCompactView,
    page,
    hasMore,
    priceErrors,

    // State setters
    setSearchTerm,
    setShowRecentlyUpdatedOnly,
    setIsCompactView,
    setPage,

    // Functions
    handleNewPriceChange,
    handleSubmit,
    fetchAllProductsForExport,
    getFilteredProducts,
    handleRemoveOfferPrice,
    handleToggleVisibility,
  };
};